import React, { useState, useEffect, useMemo, useCallback } from "react";
import copyClipboard from "../../utils/copyClipboard";
import { getCachedScript } from "../../utils/scriptCache.js";
import "./ButtonCard.css";

const ButtonCard = ({
  currentUser,
  useVisible,
  saveCepStorage,
  getCepStorage,
  useCepStorage,
  button,
  selectedTag,
  tags,
  ComponentToRender,
}) => {
  const storageKey = button._id ? button._id : button.name;
  const STORAGE_PATH = `buttons.${storageKey}`;
  const ENABLED_PATH = `${STORAGE_PATH}.enabled`;
  const OPTIONS_PATH = `${STORAGE_PATH}.options`;
  const TAGS_PATH = `${STORAGE_PATH}.tags`;

  const [isVisible, setIsVisible] = useCepStorage(ENABLED_PATH);
  const { category, name, script, argument, icon } = button;

  const [buttonTags, setButtonTags] = useState(() => {
    const savedTags = getCepStorage(TAGS_PATH);
    const initialTags = Array.isArray(savedTags)
      ? savedTags
      : Array.isArray(button.tags)
        ? button.tags
        : [];
    return initialTags;
  });

  const normalizeTagValue = (tag) => {
    if (!tag) return "";
    if (typeof tag === "string") {
      return tag.toLowerCase();
    }
    if (typeof tag === "object") {
      const raw = tag.color || tag.id || "";
      return typeof raw === "string" ? raw.toLowerCase() : "";
    }
    return "";
  };

  const buttonTagsSet = useMemo(() => {
    const set = new Set();
    buttonTags.forEach((tagValue) => {
      const normalized = normalizeTagValue(tagValue);
      if (normalized) {
        set.add(normalized);
      }
    });
    return set;
  }, [buttonTags]);

  // Use a consistent tag value for component-level filtering and child components
  const displayButtonTag = useMemo(() => {
    if (Array.isArray(button.tags) && button.tags.length > 0) {
      // prefer explicit color, fallback to id
      return button.tags[0].color || button.tags[0].id || null;
    }
    return button.buttonTag || null;
  }, [button.tags, button.buttonTag]);

  const componentButtonTag = displayButtonTag || selectedTag || null;

  const toggleButtonTag = useCallback((tag, event) => {
    event.preventDefault();
    event.stopPropagation();

    setButtonTags((prevTags) => {
      const value = normalizeTagValue(tag);
      if (!value) return prevTags;
      const newTags = new Set(prevTags.map(normalizeTagValue));
      if (newTags.has(value)) {
        newTags.delete(value);
      } else {
        newTags.add(value);
      }
      return Array.from(newTags);
    });
  }, []);

  useEffect(() => {
    saveCepStorage(TAGS_PATH, buttonTags);
  }, [buttonTags, TAGS_PATH, saveCepStorage]);

  function handleClick() {
    if (script !== "") {
      const code = getCachedScript();
      if (code) {
        window.jsx.evalScript(
          `run(${script}(${JSON.stringify(argument)}));`,
          function (result) {
            if (script === "controlsDR" && result) {
              copyClipboard(result);
            }
          },
        );
      }
    }
  }

  const generateButtonTags = useCallback(() => {
    const safeTags = Array.isArray(tags) ? tags : [];
    return (
      <div className="button-card__tags" onClick={(e) => e.stopPropagation()}>
        {safeTags.map((tag) => (
          <div
            key={tag.id}
            className={`button-tag tags-container__tag ${
              buttonTagsSet.has(normalizeTagValue(tag)) ? "active" : ""
            }`}
            onClick={(event) => toggleButtonTag(tag, event)}
            style={{
              backgroundColor: tag.color,
              cursor: "pointer",
              position: "relative",
              zIndex: 2,
            }}
            title={tag.name}
          />
        ))}
      </div>
    );
  }, [tags, buttonTagsSet, toggleButtonTag]);

  const componentTagSet = useMemo(() => {
    const set = new Set(buttonTagsSet);
    if (Array.isArray(button.tags)) {
      button.tags.forEach((t) => {
        const normalized = normalizeTagValue(t);
        if (normalized) set.add(normalized);
      });
    }
    if (displayButtonTag) {
      const normalized = normalizeTagValue(displayButtonTag);
      if (normalized) set.add(normalized);
    }
    return set;
  }, [button.tags, displayButtonTag, buttonTagsSet]);

  const normalizedSelectedTag =
    typeof selectedTag === "string" ? selectedTag.toLowerCase() : selectedTag;

  if (ComponentToRender) {
    if (normalizedSelectedTag && !componentTagSet.has(normalizedSelectedTag)) {
      return null;
    }

    return (
      <li className="button-group">
        <ComponentToRender
          useVisible={useVisible}
          saveCepStorage={saveCepStorage}
          getCepStorage={getCepStorage}
          useCepStorage={useCepStorage}
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          optionsPath={OPTIONS_PATH}
          selectedTag={selectedTag}
          buttonTag={componentButtonTag}
          buttonTags={buttonTags}
          generateButtonTags={generateButtonTags}
          category={category}
          button={button}
        />
      </li>
    );
  }

  const isButtonDisabled = false;

  if (normalizedSelectedTag && !buttonTagsSet.has(normalizedSelectedTag)) {
    return null;
  }

  return (
    <li
      className={`button-card ${isButtonDisabled ? "hidden" : ""}`}
      onClick={isButtonDisabled ? null : handleClick}
    >
      <div className="button-card__icon">
        <div dangerouslySetInnerHTML={{ __html: icon }} />
      </div>
      <div className="button-card__title">{name}</div>
      {generateButtonTags()}
    </li>
  );
};

export default React.memo(ButtonCard);
